import Sidebar from '@/components/Home_Sidebar/sidebarView.vue';
import Navbar from '@/components/Home_Navbar/NavbarView.vue';
import { get_token, getCookie } from '@/router/index.js';
import { get_api_error_message } from './platform_management_helpers';
import {
  build_platform_management_headers,
  fetch_platforms_api,
  submit_platform_config_form_api,
} from './platform_management_api';

function build_platform_config_payload(platform_config_form, routing_rules) {
  return {
    platform_id: platform_config_form.platform_id,
    app_code: platform_config_form.app_code,
    environment: platform_config_form.environment,
    base_url: platform_config_form.base_url,
    launch_url: platform_config_form.launch_url,
    callback_url: platform_config_form.callback_url,
    signature_rules: {
      signer_salt: platform_config_form.signer_salt,
      max_allowed_skew: Number(platform_config_form.max_allowed_skew || 300),
      system: platform_config_form.system,
    },
    routing_rules,
    is_valid: true,
  };
}

function create_default_platform_config_form() {
  return {
    platform_id: '',
    app_code: '',
    environment: 'development',
    base_url: '',
    launch_url: '',
    callback_url: '',
    signer_salt: '',
    max_allowed_skew: 300,
    system: '',
    routing_rules_text: '[]',
    status: 'Draft',
  };
}

export default {
  name: 'AddPlatformConfigView',
  components: {
    Sidebar,
    Navbar,
  },

  data: () => ({
    csrf_token: '',
    platforms_loading: false,
    saving_platform_config: false,
    platform_config_step: 1,
    platforms: [],
    platforms_loaded: false,
    platform_config_form: create_default_platform_config_form(),
  }),

  computed: {
    platform_options() {
      return this.platforms.map((platform) => ({
        id: platform.id,
        label: platform.name,
        app_code: platform.name,
      }));
    },
  },

  mounted() {
    this.csrf_token = getCookie('csrftoken');
    this.load_platforms();
  },

  methods: {
    require_token_or_redirect() {
      const token = get_token();
      if (!token) {
        this.$router.push({ name: 'Login' });
        return null;
      }
      return token;
    },

    build_headers(token, include_page_url = true) {
      return build_platform_management_headers(token, this.csrf_token, include_page_url);
    },

    async load_platforms() {
      const token = this.require_token_or_redirect();
      if (!token) return;

      this.platforms_loading = true;

      try {
        const result = await fetch_platforms_api(this.build_headers(token)).catch(() => null);
        if (!result?.ok) {
          this.platforms = [];
          this.platforms_loaded = true;
          this.$notyf.error(get_api_error_message(result?.response_data, 'Unable to load platforms right now.'));
          return;
        }

        const platforms = Array.isArray(result.response_data?.data) ? result.response_data.data : [];
        this.platforms = platforms.map((platform) => ({
          id: platform.id,
          name: platform.name,
          app_code: platform.name,
          environment: '',
          status: platform.is_active ? 'Connected' : 'Inactive',
        }));
        this.platforms_loaded = true;
      } finally {
        this.platforms_loading = false;
      }
    },

    normalize_platform_name(value) {
      return String(value || '')
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '');
    },

    handle_platform_change() {
      const selected_platform = this.platform_options.find(
        (platform_option) => String(platform_option.id) === String(this.platform_config_form.platform_id),
      );
      if (!selected_platform) return;

      if (!this.platform_config_form.app_code) this.platform_config_form.app_code = selected_platform.label;

      const normalized_name = this.normalize_platform_name(selected_platform.label);
      this.platform_config_form.system = normalized_name;
      this.platform_config_form.signer_salt = normalized_name ? `${normalized_name}-aic-one` : '';
    },

    go_to_step(step_number) {
      if (step_number < 1 || step_number > 4) return;
      if (step_number > this.platform_config_step && !this.validate_step(this.platform_config_step)) return;
      this.platform_config_step = step_number;
    },

    next_step() {
      if (!this.validate_step(this.platform_config_step)) return;
      if (this.platform_config_step < 4) this.platform_config_step += 1;
    },

    previous_step() {
      if (this.platform_config_step > 1) this.platform_config_step -= 1;
    },

    validate_step(step_number) {
      if (step_number === 1) {
        if (!this.platform_config_form.platform_id) {
          this.$notyf.info('Please select a platform.');
          return false;
        }

        if (!this.platform_config_form.app_code.trim()) {
          this.$notyf.info('App Code is required.');
          return false;
        }

        if (!this.platform_config_form.environment) {
          this.$notyf.info('Please select an environment.');
          return false;
        }

        return true;
      }

      if (step_number === 2) {
        if (!this.platform_config_form.base_url.trim()) {
          this.$notyf.info('Base URL is required.');
          return false;
        }

        if (!this.platform_config_form.launch_url.trim()) {
          this.$notyf.info('Launch URL is required.');
          return false;
        }

        return true;
      }

      if (step_number === 3) {
        if (!this.platform_config_form.signer_salt.trim()) {
          this.$notyf.info('Signer Salt is required.');
          return false;
        }

        if (Number.isNaN(Number(this.platform_config_form.max_allowed_skew))) {
          this.$notyf.info('Max Allowed Skew must be a valid number.');
          return false;
        }

        if (!this.platform_config_form.system.trim()) {
          this.$notyf.info('System Code is required.');
          return false;
        }

        return true;
      }

      if (step_number === 4) {
        if (!this.parse_routing_rules_text()) {
          this.$notyf.info('Routing Rules must be a valid JSON array.');
          return false;
        }

        return true;
      }

      return true;
    },

    validate_all_steps() {
      return this.validate_step(1)
        && this.validate_step(2)
        && this.validate_step(3)
        && this.validate_step(4);
    },

    parse_routing_rules_text() {
      let parsed_value = null;

      try {
        parsed_value = JSON.parse(this.platform_config_form.routing_rules_text || '[]');
      } catch (error) {
        return null;
      }

      return Array.isArray(parsed_value) ? parsed_value : null;
    },

    reset_platform_config_form() {
      this.platform_config_form = create_default_platform_config_form();
      this.platform_config_step = 1;
    },

    go_back_to_platform_management() {
      this.$router.push({ name: 'PlatformManagement' });
    },

    async submit_platform_config() {
      const token = this.require_token_or_redirect();
      if (!token || !this.validate_all_steps()) return;

      const routing_rules = this.parse_routing_rules_text();
      if (!routing_rules) {
        this.$notyf.info('Routing Rules must be a valid JSON array.');
        return;
      }

      this.saving_platform_config = true;

      try {
        const result = await submit_platform_config_form_api(
          this.build_headers(token),
          build_platform_config_payload(this.platform_config_form, routing_rules),
        );
        if (!result.ok) {
          this.$notyf.info(get_api_error_message(result.response_data, 'Create platform config endpoint is not ready yet.'));
          return;
        }

        this.$notyf.success(result.response_data?.message || 'Platform configuration saved.');
        this.reset_platform_config_form();
        this.$router.push({ name: 'PlatformManagement', query: { refresh: '1' } });
      } catch {
        this.$notyf.error('Unable to save the platform configuration right now.');
      } finally {
        this.saving_platform_config = false;
      }
    },
  },
};
